import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { resetJourney } from '@/store/reset';
import { DEFAULT_COUNTRY } from '@/locale';

import { audienceOptions } from './payinAudienceFields';
import { activityOptions } from './payinActivityFields';

export type Audience = keyof typeof audienceOptions;

export type Activity = keyof typeof activityOptions['all'];

export interface PayinState {
  audience?: Audience,
  activities: Activity[],
  activityOther: string,
  address1: string,
  address2: string,
  address3: string,
  town: string,
  postcode: string,
  country: string,
  billingSameAsPostal: boolean,
  jobTitle: string,
  establishmentType: string,
  establishmentId: string,
  establishmentName: string,
  organisationName: string,
  payinSent: boolean,
}

const initialState: Readonly<PayinState> = {
  audience: undefined,
  activities: [],
  activityOther: '',
  address1: '',
  address2: '',
  address3: '',
  town: '',
  postcode: '',
  country: DEFAULT_COUNTRY,
  billingSameAsPostal: false,
  jobTitle: '',
  establishmentType: '',
  establishmentId: '',
  establishmentName: '',
  organisationName: '',
  payinSent: false,
};

export const payinSlice = createSlice({
  name: 'payin',
  initialState,
  reducers: {
    setAudience(state: PayinState, action: PayloadAction<Audience>) {
      return {
        ...state,
        audience: action.payload,
      };
    },
    setActivities(state: PayinState, action: PayloadAction<Activity[]>) {
      return {
        ...state,
        activities: action.payload,
      };
    },
    setActivityOther(state: PayinState, action: PayloadAction<string>) {
      return {
        ...state,
        activityOther: action.payload,
      };
    },
    updatePayinFields(state: PayinState, action: PayloadAction<Partial<PayinState>>) {
      return {
        ...state,
        ...action.payload,
      };
    },
    setBillingSameAsPostal(state: PayinState, action: PayloadAction<boolean>) {
      return {
        ...state,
        billingSameAsPostal: action.payload,
      };
    },
    setPayinSent(state: PayinState, action: PayloadAction<boolean>) {
      return {
        ...state,
        payinSent: action.payload,
      };
    },
  },
  extraReducers: builder => builder.addCase(resetJourney, () => initialState),
});

export const {
  setAudience,
  setActivities,
  setActivityOther,
  updatePayinFields,
  setBillingSameAsPostal,
  setPayinSent,
} = payinSlice.actions;
